import {
    parseISO,
    format as formatDateFns,
    addDays,
    subDays,
    isValid,
    isSameDay
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

// 1. Definição dos formatos padrão (Inspirado no Vuetify/Material Design)
export type DateFormat =
    | 'fullDateWithWeekday' // "segunda-feira, 22 de janeiro de 2026"
    | 'normalDate'          // "22/01/2026"
    | 'shortDate'           // "22/01/26"
    | 'american'            // "2026-01-22"
    | 'isoDate'             // "2026-01-22T00:00:00.000Z"
    | 'monthYear'           // "janeiro de 2026"
    | 'normalDateWithWeekday' // "07 maio quarta-feira"
    | (string & {});        // Permite qualquer outra string manual se necessário

// Mapa de tradução para date-fns
const FORMAT_MAP: Record<string, string> = {
    fullDateWithWeekday: "EEEE, d 'de' MMMM 'de' yyyy",
    normalDate: 'dd/MM/yyyy',
    shortDate: 'dd/MM/yy',
    american: 'yyyy-MM-dd',
    isoDate: "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
    monthYear: "MMMM 'de' yyyy",
    normalDateWithWeekday: "dd MMM EEEE",
};

export type DateInput = string | Date | number | null | undefined;

export interface IDateAdapter {
    now(): Date;
    toJSDate(date: DateInput): Date | null;
    format(
        date: DateInput,
        formatName?: DateFormat,
        placeholder?: string
    ): string;
    addDays(date: DateInput, days: number): string;
    subDays(date: DateInput, days: number): string;
    isSameDay(dateA: DateInput, dateB: DateInput): boolean;
    timeToDate(time: string, baseDate?: DateInput): Date | null;
    isAfterTime(
        time: string,
        baseDate?: DateInput,
        now?: Date
    ): boolean;
    isBetweenTimes(
        start: string,
        end: string,
        baseDate?: DateInput,
        now?: Date
    ): boolean;
}


export class DateAdapter implements IDateAdapter {
    private static _instance: DateAdapter | null = null;

    static instance(): DateAdapter {
        if (!this._instance) {
            this._instance = new DateAdapter();
        }
        return this._instance;
    }

    now(): Date {
        return new Date();
    }

    toJSDate(date: DateInput): Date | null {
        if (date === null || date === undefined) return null;
    
        const parsed =
            date instanceof Date
                ? date
                : typeof date === 'number'
                    ? new Date(date)
                    : parseISO(date);
    
        return isValid(parsed) ? parsed : null;
    }

    format(
        date: DateInput,
        formatName: DateFormat = 'normalDate',
        placeholder = ''
    ): string {
        const d = this.toJSDate(date);
        if (!d) return placeholder;

        const formatString = FORMAT_MAP[formatName] || formatName;

        return formatDateFns(d, formatString, { locale: ptBR });
    }

    addDays(date: DateInput, days: number): string {
        const d = this.toJSDate(date);
        if (!d) return '';
        return this.format(addDays(d, days), 'isoDate');
    }

    subDays(date: DateInput, days: number): string {
        const d = this.toJSDate(date);
        if (!d) return '';
        return this.format(subDays(d, days), 'isoDate');
    }

    isSameDay(dateA: DateInput, dateB: DateInput): boolean {
        const d1 = this.toJSDate(dateA);
        const d2 = this.toJSDate(dateB);
        if (!d1 || !d2) return false;
        return isSameDay(d1, d2);
    }

    timeToDate(time: string, baseDate: DateInput = this.now()): Date | null {
        const date = this.toJSDate(baseDate);
        if (!date || !time) return null;

        const [h, m, s = '0'] = time.split(':');
        const seconds = Number(s.split('.')[0]);

        const hours = Number(h);
        const minutes = Number(m);

        if (
            [hours, minutes, seconds].some(Number.isNaN) ||
            hours < 0 || hours > 23 ||
            minutes < 0 || minutes > 59 ||
            seconds < 0 || seconds > 59
        ) {
            return null;
        }

        const result = new Date(date);
        result.setHours(hours, minutes, seconds, 0);

        return result;
    }

    isAfterTime(
        time: string,
        baseDate: DateInput = this.now(),
        now: Date = this.now()
    ): boolean {
        const target = this.timeToDate(time, baseDate);
        return !!target && now > target;
    }

    isBetweenTimes(
        start: string,
        end: string,
        baseDate: DateInput = this.now(),
        now: Date = this.now()
    ): boolean {
        const startDate = this.timeToDate(start, baseDate);
        const endDate = this.timeToDate(end, baseDate);
        if (!startDate || !endDate) return false;

        return now >= startDate && now <= endDate;
    }
}
